import type React from "react"
import { type HTMLAttributes, type ReactNode, useCallback, useMemo } from "react"
import { Input } from "@/shared/components"
import type { InputProps } from "@/shared/components/control/input"
import { useCurrentUser } from "@/shared/hooks/use-current-user.ts"
import { cn } from "@/shared/lib/utils.ts"

export type ICustomOnChangeResult = { [key: string]: string | number }

interface ICommonProps extends HTMLAttributes<HTMLTableCellElement> {
  valueType?: "string" | "number"
}

interface IReadonlyCell {
  readonly: true
  customOnChange?: never
  inputProps?: Omit<InputProps, "onChange">
}

interface IEditableCell {
  inputProps: InputProps
  readonly?: false
  customOnChange: (data: ICustomOnChangeResult) => void
}

interface IRenderCell {
  inputProps?: never
  readonly?: never
  render: () => ReactNode
  customOnChange?: never
}

export type ITableEditableCell = ICommonProps & (IReadonlyCell | IEditableCell | IRenderCell)

export function TableEditableCell({ inputProps, valueType = "number", customOnChange, readonly, ...props }: ITableEditableCell) {
  const { isCompany } = useCurrentUser()

  const mergedProps = useMemo(
    () => ({
      ...props,
      className: cn(`!px-0 !py-0 bg-zinc-50 dark:bg-zinc-700`, props.className, isCompany && "dark:bg-zinc-500")
    }),
    [props]
  )

  if (readonly)
    return (
      <td {...mergedProps}>
        <span className="pl-4">{inputProps?.value}</span>
      </td>
    )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value
      const fieldName = e.target.name

      let parsedValue: string | number

      if (valueType === "number") {
        // If value is empty string, treat as 0
        if (rawValue === "") {
          parsedValue = 0
        } else if (!/^-?\d*\.?\d*$/.test(rawValue)) {
          // Ignore invalid numeric input like letters or multiple dots
          return
        } else {
          parsedValue = Number(rawValue)
        }
      } else {
        parsedValue = rawValue
      }

      if (customOnChange && fieldName) {
        customOnChange({ [fieldName]: parsedValue })
      } else if (inputProps?.onChange) {
        inputProps.onChange(e)
      }
    },
    [customOnChange, inputProps?.onChange, valueType]
  )

  return (
    <td {...mergedProps}>
      <Input
        autoComplete="off"
        disabled={isCompany}
        placeholder={valueType === "string" ? "" : "0"}
        labelProps={{ className: "!rounded-none flex w-full !p-0 border-none" }}
        className={cn(
          "mx-auto h-[calc(100%-4px)] w-[calc(100%-4px)!important] px-3 ring-blue-400 transition focus:outline-none focus:ring-2 disabled:bg-white disabled:dark:bg-zinc-500",
          inputProps?.className,
          isCompany ? "size-full" : "h-[calc(100%-4px)] w-[calc(100%-4px)!important]"
        )}
        {...inputProps}
        onChange={handleChange}
      />
    </td>
  )
}
